import { useState } from 'react';
import { X, UploadCloud } from 'lucide-react';
import { DOCUMENT_CATEGORIES, ALLOWED_FILE_EXTENSIONS, MAX_FILE_SIZE_BYTES } from '../../constants/documentCategories';
import { getChurches } from '../../lib/churches';
import { uploadDocument } from '../../lib/documents';
import { useAuth } from '../../context/AuthContext';

const YEARS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i);

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const inputClass =
  'w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm text-[#00384D] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#007A96]/30 focus:border-[#007A96] transition-colors';
const labelClass = 'block text-sm font-medium text-[#00384D] mb-1.5';

export default function DocumentUpload({ onClose, onUploaded }) {
  const { user } = useAuth();
  const churches = getChurches();

  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState(DOCUMENT_CATEGORIES[0]);
  const [churchId, setChurchId] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  function handleFileChange(e) {
    const selected = e.target.files?.[0];
    setError('');
    if (!selected) return;

    const ext = `.${selected.name.split('.').pop().toLowerCase()}`;
    if (!ALLOWED_FILE_EXTENSIONS.includes(ext)) {
      setError(`Unsupported file type. Allowed: ${ALLOWED_FILE_EXTENSIONS.join(', ')}`);
      return;
    }
    if (selected.size > MAX_FILE_SIZE_BYTES) {
      setError(`File is too large. Maximum size is ${formatBytes(MAX_FILE_SIZE_BYTES)}.`);
      return;
    }

    setFile(selected);
    if (!title) setTitle(selected.name.replace(/\.[^/.]+$/, ''));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!file) {
      setError('Please choose a file to upload.');
      return;
    }
    if (!title.trim()) {
      setError('Please enter a document title.');
      return;
    }

    const selectedChurch = churches.find((c) => c.id === churchId);

    setUploading(true);
    try {
      await uploadDocument({
        file,
        title: title.trim(),
        category,
        churchId: churchId || null,
        churchName: selectedChurch?.name,
        year,
        description: description.trim(),
        userId: user?.id,
      });
      onUploaded();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#00384D]/50 backdrop-blur-[2px] px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-slate-200">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
          <div>
            <h2 className="font-semibold text-[#00384D] text-base">Upload Document</h2>
            <p className="text-xs text-slate-400 mt-0.5">Add a new file to the district archive</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-[#00384D] hover:bg-slate-100 rounded-md p-1.5 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {error && (
            <div className="rounded-md border border-[#C21E26]/20 bg-[#C21E26]/5 px-4 py-3 text-sm text-[#C21E26]">
              {error}
            </div>
          )}

          {/* File picker */}
          <div>
            <label className={labelClass}>File</label>
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-lg py-7 cursor-pointer hover:border-[#007A96]/40 hover:bg-[#007A96]/[0.03] transition-colors">
              <div className="h-9 w-9 rounded-full bg-[#4EA6B3]/10 flex items-center justify-center">
                <UploadCloud className="h-4 w-4 text-[#007A96]" strokeWidth={1.75} />
              </div>
              <span className="text-sm text-slate-600 font-medium">
                {file ? file.name : 'Click to choose a file'}
              </span>
              {!file && <span className="text-xs text-slate-400">PDF, Word, Excel, PowerPoint, JPG, PNG</span>}
              <input type="file" onChange={handleFileChange} className="hidden" />
            </label>
            {file && (
              <p className="text-xs text-slate-400 mt-1.5">
                {file.type || 'Unknown type'} · {formatBytes(file.size)}
              </p>
            )}
          </div>

          {/* Title */}
          <div>
            <label htmlFor="title" className={labelClass}>
              Document Title
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="2026 Annual District Report"
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Category */}
            <div>
              <label htmlFor="category" className={labelClass}>
                Category
              </label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className={inputClass}
              >
                {DOCUMENT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Year */}
            <div>
              <label htmlFor="year" className={labelClass}>
                Year
              </label>
              <select
                id="year"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className={inputClass}
              >
                {YEARS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Church */}
          <div>
            <label htmlFor="church" className={labelClass}>
              Church
            </label>
            <select
              id="church"
              value={churchId}
              onChange={(e) => setChurchId(e.target.value)}
              className={inputClass}
            >
              <option value="">District-wide</option>
              {churches.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className={labelClass}>
              Description <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className={`${inputClass} resize-none`}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-slate-100 -mx-6 px-6 pb-0 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-[#00384D] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="bg-[#00384D] text-white text-sm font-medium rounded-md px-5 py-2.5 hover:bg-[#00495F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Uploading…' : 'Upload Document'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
